import React, { useState, useEffect, useCallback } from "react";
import axiosWithAuth from "../../utils/axiosWithAuth";
import { Table, Button, Modal, Badge, Spinner, Card, Row, Col, Form } from "react-bootstrap";
import { getApiEndpoint } from "../../utils/apiConfig";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import { SquareLoader } from "react-spinners";
import "./MyOrders.scss";
import { useNavigate } from "react-router-dom";

const MyOrders = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState({ startDate: "", endDate: "" });
  const pageSize = 10;

  // Fetch user's orders from API
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const api = axiosWithAuth();
      
      // Build query parameters
      let queryParams = `?page=${currentPage}&limit=${pageSize}`;
      
      // Add status filter if not "all"
      if (statusFilter !== "all") {
        queryParams += `&status=${statusFilter === "active" ? "true" : "false"}`;
      }
      
      // Add date filters if provided
      if (dateFilter.startDate) {
        queryParams += `&startDate=${dateFilter.startDate}`;
      }
      if (dateFilter.endDate) {
        queryParams += `&endDate=${dateFilter.endDate}`;
      }
      
      // Add includeDetails parameter to get more data
      queryParams += '&includeDetails=true';
      
      // Use the user-specific orders endpoint (similar to admin but for user)
      const endpoint = getApiEndpoint(`repairs/orders/user${queryParams}`);
      console.log("Fetching user orders from:", endpoint);
      
      const response = await api.get(endpoint);
      console.log("API Response:", response.data);
      
      if (response.data && response.data.data) {
        setOrders(response.data.data);
        setTotalCount(response.data.count || 0);
        setTotalPages(Math.ceil((response.data.count || 0) / pageSize) || 1);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      // Don't show alert to user, just log the error
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, dateFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // View order details
  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
    const detailId = order.breakdownId || order.id || order._id;
    fetchOrderDetails(detailId);
  };

  // Fetch order details
  const fetchOrderDetails = async (orderId) => {
    setLoadingDetails(true);
    try {
      const api = axiosWithAuth();
      const endpoint = getApiEndpoint(`repairs/breakdown-materials/${orderId}`);
      const response = await api.get(endpoint);
      
      if (!response.data) {
        throw new Error('Invalid response format');
      }

      const orderData = {
        _id: orderId,
        ...response.data,
        breakdown: response.data.breakdown || {},
        costs: response.data.costs || {},
        currency: response.data.costs?.currency || "AZN"
      };
      
      if (!orderData.breakdown.rooms || !Array.isArray(orderData.breakdown.rooms)) {
        orderData.breakdown.rooms = [];
      }
      
      // Process the rooms to ensure they have proper area values
      orderData.breakdown.rooms = orderData.breakdown.rooms.map(room => {
        if (!room.area || parseFloat(room.area) <= 0) {
          let calculatedArea = 0;
          
          if (Array.isArray(room.materials) && room.materials.length > 0) {
            calculatedArea = room.materials.reduce((sum, material) => {
              const materialArea = parseFloat(material.area || 0);
              return sum + (isNaN(materialArea) || materialArea <= 0 ? 1 : materialArea);
            }, 0);
          }
          
          calculatedArea = Math.max(calculatedArea, 1);
          
          return {
            ...room,
            area: calculatedArea
          };
        }
        return room;
      });
      
      // Ensure we have fixtures data
      if (!orderData.breakdown.fixtures) {
        orderData.breakdown.fixtures = {};
      }
      
      setOrderDetails(orderData);
    } catch (error) {
      console.error("Failed to fetch order details:", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString('az-AZ');
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0.00';
    const numValue = typeof num === 'string' ? parseFloat(num) : num;
    const rounded = Math.round(numValue * 100) / 100;
    return new Intl.NumberFormat('az-AZ').format(rounded);
  };

  const getStatusBadge = (status) => {
    return (
      <Badge bg={status ? "success" : "warning"}>
        {status ? t("orders.active") : t("orders.pending")}
      </Badge>
    );
  };

  const getRoomCount = (order) => {
    const rooms = 
      order.rooms || 
      order.breakdown?.rooms || 
      order.breakdownResponse?.breakdown?.rooms || [];
    
    return rooms.length || 0;
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    setDateFilter(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    const pages = [];
    const maxButtons = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);
    
    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }
    
    // Previous button
    pages.push(
      <li key="prev" className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
        <Button 
          className="page-link" 
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          variant="outline-primary"
        >
          &laquo;
        </Button>
      </li>
    );
    
    // Page buttons
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
          <Button 
            className="page-link" 
            onClick={() => handlePageChange(i)}
            variant={currentPage === i ? "primary" : "outline-primary"}
          >
            {i}
          </Button>
        </li>
      );
    }
    
    // Next button
    pages.push(
      <li key="next" className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
        <Button 
          className="page-link" 
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          variant="outline-primary"
        >
          &raquo;
        </Button>
      </li>
    );
    
    return (
      <nav className="d-flex justify-content-center mt-4">
        <ul className="pagination mb-0">
          {pages}
        </ul>
      </nav>
    );
  };

  if (loading) {
    return (
      <div className="loader-container">
        <SquareLoader color="#cda742" size={80} />
      </div>
    );
  }

  return (
    <div className="my-orders-section">
      <Header
        title={t("myOrders.title")}
        showProfileIcon={true}
        showBackIcon={true}
        onBackClick={() => navigate("/account/myhome")}
      />

      <div className="orders-content">
        <Card className="shadow-sm">
          <Card.Header className="bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-shopping-cart me-2 text-primary"></i>
                {t("orders.myOrders")}
              </h5>
              <Badge bg="secondary">{totalCount} {t("orders.total")}</Badge>
            </div>
          </Card.Header>
          
          <Card.Body>
            {/* Filters */}
            <div className="filters-section mb-4">
              <Row>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>{t("orders.status")}</Form.Label>
                    <Form.Select 
                      value={statusFilter} 
                      onChange={handleStatusFilterChange}
                    >
                      <option value="all">{t("orders.allStatuses")}</option>
                      <option value="active">{t("orders.active")}</option>
                      <option value="inactive">{t("orders.pending")}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>{t("orders.startDate")}</Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={dateFilter.startDate}
                      onChange={handleDateFilterChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>{t("orders.endDate")}</Form.Label>
                    <Form.Control
                      type="date"
                      name="endDate"
                      value={dateFilter.endDate}
                      onChange={handleDateFilterChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Orders Table */}
            {orders.length === 0 ? (
              <div className="empty-state">
                <div className="text-center py-5">
                  <i className="fas fa-shopping-cart fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">{t("orders.noOrders")}</h5>
                  <p className="text-muted">{t("orders.noOrdersDescription")}</p>
                </div>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <Table hover className="orders-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t("orders.orderDate")}</th>
                        <th>{t("orders.repairStyle")}</th>
                        <th>{t("orders.repairTariff")}</th>
                        <th className="text-center">{t("orders.rooms")}</th>
                        <th className="text-end">{t("orders.totalCost")}</th>
                        <th className="text-center">{t("orders.status")}</th>
                        <th className="text-center">{t("orders.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order, index) => (
                        <tr key={order._id || order.id} className="order-row">
                          <td className="fw-medium">
                            {(currentPage - 1) * pageSize + index + 1}
                          </td>
                          <td>
                            <div className="date-info">
                              <div>{formatDate(order.createdAt)}</div>
                            </div>
                          </td>
                          <td>
                            <span className="style-badge">
                              {order.repairStyle?.name?.az || order.repairStyle?.name?.text || t("orders.notSpecified")}
                            </span>
                          </td>
                          <td>
                            <span className="tariff-badge">
                              {order.repairTariff?.name?.az || order.repairTariff?.name?.text || t("orders.notSpecified")}
                            </span>
                          </td>
                          <td className="text-center">
                            <Badge bg="info" className="rooms-badge">
                              <i className="fas fa-door-open me-1"></i>
                              {getRoomCount(order)}
                            </Badge>
                          </td>
                          <td className="text-end">
                            <div className="price-info">
                              <span className="price-amount">
                                {formatNumber(order.materialsCost || order.costs?.materialsCost || 0)}
                              </span>
                              <span className="currency"> {order.currency || "AZN"}</span>
                            </div>
                          </td>
                          <td className="text-center">
                            {getStatusBadge(order.status)}
                          </td>
                          <td className="text-center">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleViewDetails(order)}
                              className="view-details-btn"
                            >
                              <i className="fas fa-eye me-1"></i>
                              {t("orders.viewDetails")}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                
                {/* Pagination and info */}
                <div className="pagination-section">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="pagination-info">
                      <small className="text-muted">
                        {t("orders.showing")} {Math.min((currentPage - 1) * pageSize + 1, totalCount)} - {Math.min(currentPage * pageSize, totalCount)} {t("orders.of")} {totalCount} {t("orders.entries")}
                      </small>
                    </div>
                    {renderPagination()}
                  </div>
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Order Details Modal */}
      <Modal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        size="xl"
        centered
        className="order-details-modal"
      >
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            <i className="fas fa-receipt me-2 text-primary"></i>
            {t("orders.orderDetails")} #{selectedOrder?.id || selectedOrder?._id}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetails ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">{t("orders.loadingDetails")}</p>
            </div>
          ) : orderDetails ? (
            <div className="order-details-content">
              {/* Summary Section */}
              <Card className="mb-4">
                <Card.Header>
                  <h6 className="mb-0">
                    <i className="fas fa-chart-pie me-2"></i>
                    {t("orders.summary")}
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <div className="cost-card bg-light p-3 rounded text-center">
                        <div className="cost-icon mb-2">
                          <i className="fas fa-hammer text-warning fa-2x"></i>
                        </div>
                        <h6>{t("orders.materialsCost")}</h6>
                        <div className="cost-amount">
                          {formatNumber(orderDetails.costs?.materialsCost || 0)} {orderDetails.currency}
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="cost-card bg-light p-3 rounded text-center">
                        <div className="cost-icon mb-2">
                          <i className="fas fa-tools text-info fa-2x"></i>
                        </div>
                        <h6>{t("orders.serviceCost")}</h6>
                        <div className="cost-amount">
                          {formatNumber(orderDetails.costs?.serviceCost || 0)} {orderDetails.currency}
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="cost-card bg-light p-3 rounded text-center">
                        <div className="cost-icon mb-2">
                          <i className="fas fa-door-open text-secondary fa-2x"></i>
                        </div>
                        <h6>{t("orders.fixtures")}</h6>
                        <div className="cost-amount">
                          {formatNumber(
                            (orderDetails.breakdown?.fixtures?.doors?.materialCost || 0) + 
                            (orderDetails.breakdown?.fixtures?.doors?.serviceCost || 0) +
                            (orderDetails.breakdown?.fixtures?.windows?.materialCost || 0) + 
                            (orderDetails.breakdown?.fixtures?.windows?.serviceCost || 0)
                          )} {orderDetails.currency}
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="cost-card bg-primary text-white p-3 rounded text-center">
                        <div className="cost-icon mb-2">
                          <i className="fas fa-calculator fa-2x"></i>
                        </div>
                        <h6>{t("orders.totalCost")}</h6>
                        <div className="cost-amount fw-bold">
                          {formatNumber(orderDetails.costs?.generalCost || 0)} {orderDetails.currency}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Rooms Section */}
              <Card>
                <Card.Header>
                  <h6 className="mb-0">
                    <i className="fas fa-th-large me-2"></i>
                    {t("orders.rooms")} ({orderDetails.breakdown.rooms.length})
                  </h6>
                </Card.Header>
                <Card.Body>
                  {orderDetails.breakdown.rooms.length === 0 ? (
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle me-2"></i>
                      {t("orders.noRoomsData")}
                    </div>
                  ) : (
                    <div className="accordion" id="roomsAccordion">
                      {orderDetails.breakdown.rooms.map((room, index) => (
                        <div className="accordion-item" key={index}>
                          <h2 className="accordion-header">
                            <button
                              className="accordion-button collapsed"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#room-${index}`}
                            >
                              <div className="d-flex justify-content-between w-100 me-3">
                                <span>
                                  <i className="fas fa-door-open me-2"></i>
                                  {room.roomType || `${t("orders.room")} ${index + 1}`} 
                                  <small className="text-muted ms-2">({formatNumber(room.area || 0)} m²)</small>
                                </span>
                                <Badge bg="primary">
                                  {formatNumber(room.totalCost || 0)} {orderDetails.currency}
                                </Badge>
                              </div>
                            </button>
                          </h2>
                          <div
                            id={`room-${index}`}
                            className="accordion-collapse collapse"
                            data-bs-parent="#roomsAccordion"
                          >
                            <div className="accordion-body">
                              {!room.materials || room.materials.length === 0 ? (
                                <div className="alert alert-warning">
                                  <i className="fas fa-exclamation-triangle me-2"></i>
                                  {t("orders.noMaterialsData")}
                                </div>
                              ) : (
                                <Table striped bordered hover size="sm">
                                  <thead>
                                    <tr>
                                      <th>{t("orders.material")}</th>
                                      <th>{t("orders.quantity")}</th>
                                      <th className="text-end">{t("orders.price")}</th>
                                      <th className="text-end">{t("orders.total")}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {room.materials.map((material, mIndex) => (
                                      <tr key={mIndex}>
                                        <td>
                                          <div className="material-info">
                                            <div className="material-name">
                                              {material.name?.text || material.name?.az || material.materialName || ""}
                                            </div>
                                            <small className="text-muted">
                                              {material.unitOfMeasure || "unit"}
                                            </small>
                                          </div>
                                        </td>
                                        <td>
                                          {formatNumber(material.area || material.quantity || material.count || 0)}
                                        </td>
                                        <td className="text-end">
                                          {formatNumber(material.price || 0)} {material.currency || orderDetails.currency}
                                        </td>
                                        <td className="text-end fw-medium">
                                          {formatNumber(
                                            (material.price || 0) * (material.area || material.quantity || material.count || 1)
                                          )} {material.currency || orderDetails.currency}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          ) : (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {t("orders.noDetailsAvailable")}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            <i className="fas fa-times me-1"></i>
            {t("common.close")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MyOrders; 